import {
  configureChains,
  createClient,
  //InjectedConnector,
  Client,
  WebSocketProvider,
  createStorage,
} from '@wagmi/core';
//import { WalletConnectConnector } from '@wagmi/core/connectors/walletConnect';
import { Web3Modal } from '@web3modal/html';
import {
  EthereumClient,
  w3mConnectors,
  w3mProvider,
} from '@web3modal/ethereum';
import {
  ArnConnector,
  Web3ModalV2ConnectorOpts,
  Web3ModalV2DesktopBehaviorOpts,
  Web3ModalV2DesktopBehaviorMode,
} from '@arianee/arn-types';
import { ethers, TypedDataDomain, TypedDataField } from 'ethers';
import { ArnClientError } from '../../../ArnClientError';
import {
  querySelectorAllDeep,
  querySelectorDeep,
} from 'query-selector-shadow-dom';
import { poaCore } from '../../../wagmi';
import { v4 as uuidv4 } from 'uuid';

/*
const HTML_TEMPLATE_UNIVERSAL_LINK_WINDOW = `
  <h1>This tab will automatically be closed once your wallet is connected.</h1>
  <h2>If this not the case, please close it manually to continue.</h2>
`;
*/

type UpdateQrFunction = (
  w3mQrCode: HTMLElement,
  universalLink: string,
  injectorInt: NodeJS.Timer,
  logoSrc?: string,
  logoSrc_dark?: string,
  autoClear?: boolean
) => void;

export class Web3ModalV2Connector implements ArnConnector {
  static readonly WALLETCONNECT_KEY = 'walletconnect';
  static readonly WALLETCONNECT_DEEPLINK_CHOICE_KEY =
    'WALLETCONNECT_DEEPLINK_CHOICE';
  static readonly W3M_RECENT_WALLET = 'W3M_RECENT_WALLET';
  static readonly DEFAULT_WAGMI_PREFIX_KEY = 'wagmi';

  protected wagmiPrefixKey: string;
  protected uuid: string | undefined;
  protected readonly wagmiClient: Client<
    (ethers.providers.FallbackProvider | ethers.providers.JsonRpcProvider) & {
      chains: any[];
      pollingInterval: number;
    },
    WebSocketProvider
  >;
  protected readonly ethereumClient: EthereumClient;
  protected readonly web3Modal: Web3Modal;

  isConnected = false;
  provider?: ethers.providers.Web3Provider;

  private readonly desktopBehaviorOpts: Web3ModalV2DesktopBehaviorOpts;
  private readonly removeDesktopWallets: boolean = false;

  protected log(..._data: any[]) {
    // console.log(`[${this.constructor.name}]`, ...data);
  }

  constructor(options: Web3ModalV2ConnectorOpts) {
    if (!options.projectId) {
      throw new Error('Missing projectId');
    }
    if (
      options.desktopBehavior &&
      options.desktopBehavior.mode !==
        Web3ModalV2DesktopBehaviorMode.WalletConnectOnly &&
      !options.desktopBehavior.universalLink
    ) {
      throw new Error('Missing desktopBehavior.universalLink');
    }
    const version = options.wcVersion;
    if (version === 1) {
      console.warn('You are using a deprecated version of WalletConnect (v1)');
    }
    // Save desktopBehavior options or use default
    if (options.desktopBehavior) {
      this.desktopBehaviorOpts = options.desktopBehavior;
    } else {
      this.desktopBehaviorOpts = {
        mode: Web3ModalV2DesktopBehaviorMode.WalletConnectOnly,
      };
    }
    // Save removeDesktopWallets option or use default
    if (options.removeDesktopWallets) {
      this.removeDesktopWallets = options.removeDesktopWallets;
    }
    // Save wagmiPrefixKey option or use default
    if (options.storagePrefix && options.storagePrefix.length > 0) {
      this.wagmiPrefixKey = options.storagePrefix;
    } else {
      this.wagmiPrefixKey = Web3ModalV2Connector.DEFAULT_WAGMI_PREFIX_KEY;
    }
    // If no chains are specified, use the default ones
    if (!options.chains?.length) {
      options.chains = [poaCore];
    }
    // Unable to manage properly manage two walletconnect sessions at the same time
    localStorage.removeItem(Web3ModalV2Connector.WALLETCONNECT_KEY);

    // Partially clear cache to avoid conflicts between the different instances of Web3Modal
    localStorage.removeItem(
      Web3ModalV2Connector.WALLETCONNECT_DEEPLINK_CHOICE_KEY
    );
    localStorage.removeItem(Web3ModalV2Connector.W3M_RECENT_WALLET);

    // Wagmi Core Client
    const { provider } = configureChains(options.chains, [
      w3mProvider({ projectId: options.projectId }),
    ]);

    /**
     * Previously used connectors, keep them for reference
     * The old LedgerConnector and CoinbaseWalletConnector made the build fail
     * In the new version they seem to have been removed, so we can use w3mConnectors as intended
     */
    /*
    const _connectors = [
      new WalletConnectConnector({
        chains: options.chains,
        options: {
          projectId: options.projectId,
          // storageId: Web3ModalV2Connector.WALLETCONNECT_KEY, // ${this.wagmiPrefixKey}.${Web3ModalV2Connector.WALLETCONNECT_KEY}
        },
      }),
      new InjectedConnector({
        chains: options.chains,
        options: { shimDisconnect: true },
      }),
      // LedgerConnector, CoinbaseWalletConnector, etc.
      // For more connectors, see: https://github.com/wagmi-dev/references/tree/main/packages/connectors/src
    ];
    */

    this.wagmiClient = createClient({
      autoConnect: false,
      storage: createStorage({
        storage: window.localStorage,
        key: this.wagmiPrefixKey,
      }),
      connectors: w3mConnectors({
        projectId: options.projectId,
        version,
        chains: options.chains,
      }),
      provider,
    }) as any;

    // Ethereum Client
    this.ethereumClient = new EthereumClient(this.wagmiClient, options.chains);

    // Transform options to a regular Web3ModalConfig object before instantiating Web3Modal
    const { chains, desktopBehavior, ...web3ModalConfig } = options;

    // Web3Modal
    this.web3Modal = new Web3Modal({ ...web3ModalConfig }, this.ethereumClient);

    const unsubscribeModal1 = this.web3Modal.subscribeModal((modal) => {
      if (modal.open) {
        setTimeout(() => {
          const w3mModals = document.querySelectorAll('w3m-modal');
          if (w3mModals.length > 0) {
            const lastModal = w3mModals[w3mModals.length - 1];
            if (lastModal) {
              const uuid = uuidv4();
              lastModal.setAttribute('arn-uuid', uuid);
              this.uuid = uuid;
            }
          } else {
            console.warn(`Could not found any 'w3m-modal' element`);
          }
          unsubscribeModal1();
        });
      }
    });

    const unsubscribeModal2 = this.web3Modal.subscribeModal((modal) => {
      if (!modal.open) {
        this.removeWeb3ModalUI();
        this.clearIntervals();
        unsubscribeModal2();
      }
    });
  }

  private qrInjectorInt: NodeJS.Timeout | null = null;
  private desktopWalletsInjectorInt: NodeJS.Timeout | null = null;
  private modalInjectorInt: NodeJS.Timeout | null = null;
  // private localStorageInjectorInt: NodeJS.Timeout | null = null;

  async connect(): Promise<void> {
    if (this.isConnected) {
      this.log('Already connected'); // TODO: Throw error
      return;
    }
    const clientData = await this.wagmiClient.autoConnect();
    if (clientData) {
      const web3Provider = this.getWeb3Provider(clientData);
      this.log('Using cached provider');

      this.provider = web3Provider;
      this.log(
        'Connected',
        this.wagmiClient.connector?.name,
        this.provider?.connection?.url
      );
    } else {
      this.log('No cached provider found');

      return new Promise((resolve, reject) => {
        const isMobile = window.matchMedia('(max-width: 767px)').matches;
        if (isMobile) {
          this.log('Mobile detected');
        }

        switch (this.desktopBehaviorOpts.mode) {
          case Web3ModalV2DesktopBehaviorMode.ArianeeOrWalletConnect:
            this.qrInjectorInt = this.attachQRInjector(
              this.desktopBehaviorOpts.universalLink!,
              this.updateQRChoice.bind(this),
              this.desktopBehaviorOpts.primaryLogoUrl,
              this.desktopBehaviorOpts.primaryLogoUrl_dark
            );
            break;

          case Web3ModalV2DesktopBehaviorMode.ArianeeOnly:
            this.qrInjectorInt = this.attachQRInjector(
              this.desktopBehaviorOpts.universalLink!,
              this.updateQR.bind(this),
              this.desktopBehaviorOpts.primaryLogoUrl,
              this.desktopBehaviorOpts.primaryLogoUrl_dark
            );
            break;
        }

        // Desktop
        if (!isMobile) {
          if (this.removeDesktopWallets) {
            this.desktopWalletsInjectorInt = this.attachDesktopWalletInjector();
          }
        }

        // Mobile
        if (isMobile) {
          // this.overrideWindowOpen();
          // this.localStorageInjectorInt = this.attachLocalStorageInjector();
        }

        this.modalInjectorInt = this.attachModalInjector();
        // We can auto-clear the interval after 2.5s because it's not used anymore
        setTimeout(() => {
          if (this.modalInjectorInt) {
            clearInterval(this.modalInjectorInt);
          }
        }, 2500);

        let unsubscribeClient: (() => void) | null = null;
        let unsubscribeModal: (() => void) | null = null;

        const unsubscribe = () => {
          if (unsubscribeClient) {
            unsubscribeClient();
          }
          if (unsubscribeModal) {
            unsubscribeModal();
          }
        };

        unsubscribeClient = this.wagmiClient.subscribe((client) => {
          if (client?.status === 'connected') {
            const web3Provider = this.getWeb3Provider(client.data);
            if (!web3Provider) {
              throw new Error('No provider found after connection');
            }

            this.provider = web3Provider;
            this.isConnected = true;
            this.log(
              'Connected',
              this.wagmiClient.connector?.name,
              this.provider?.connection?.url
            );

            unsubscribe();
            this.removeWeb3ModalUI();
            this.clearIntervals();

            setTimeout(() => resolve());
          }
        });

        unsubscribeModal = this.web3Modal.subscribeModal((modal) => {
          if (modal?.open === false) {
            unsubscribe();

            setTimeout(() => {
              if (!this.isConnected) {
                reject(new ArnClientError('Modal closed'));
              }
            });
          }
        });

        // Open Web3Modal
        this.web3Modal.openModal();
      });
    }
  }

  onConnectFromCache(): Promise<void> {
    return new Promise((resolve) => {
      setTimeout(() => {
        this.removeWeb3ModalUI('w3m-modal');
        resolve();
      }, 650);
    });
  }

  private clearIntervals() {
    if (this.qrInjectorInt) {
      clearInterval(this.qrInjectorInt);
    }
    if (this.desktopWalletsInjectorInt) {
      clearInterval(this.desktopWalletsInjectorInt);
    }
    /*
    if (this.localStorageInjectorInt) {
      clearInterval(this.localStorageInjectorInt);
    }
    */
    if (this.modalInjectorInt) {
      clearInterval(this.modalInjectorInt);
    }
  }

  protected async checkConnected() {
    if (!this.isConnected) {
      await this.connect();
    }
  }

  async signMessage(message: string): Promise<string> {
    this.log(`SignMessage << "${message}"`);
    await this.checkConnected();

    const signedMsg = await this.provider!.getSigner().signMessage(message);
    this.log(`SignMessage >> "${signedMsg}"`);

    return signedMsg;
  }

  async signTypedData(
    domain: TypedDataDomain,
    types: Record<string, Array<TypedDataField>>,
    value: Record<string, any>
  ) {
    this.log(`SignTypedData << "${JSON.stringify({ domain, types, value })}"`);
    await this.checkConnected();

    const signedTypedData = await this.provider!.getSigner()._signTypedData(
      domain,
      types,
      value
    );
    this.log(`SignTypedData >> "${JSON.stringify(signedTypedData)}"`);

    return signedTypedData;
  }

  async signTransaction(
    transaction: ethers.utils.Deferrable<ethers.providers.TransactionRequest>
  ) {
    this.log(`SignTransaction << "${JSON.stringify(transaction)}"`);
    await this.checkConnected();

    const signedTx = await this.provider!.getSigner().signTransaction(
      transaction
    );
    this.log(`SignTransaction >> "${JSON.stringify(signedTx)}"`);

    return signedTx;
  }

  async sendTransaction(
    transaction: ethers.utils.Deferrable<ethers.providers.TransactionRequest>
  ) {
    this.log(`SendTransaction << "${JSON.stringify(transaction)}"`);
    await this.checkConnected();

    const txRes = await this.provider!.getSigner().sendTransaction(transaction);
    this.log(`SendTransaction >> "${txRes?.hash ?? 'undefined'}"`);

    return txRes;
  }

  async sendUncheckedTransaction(
    transaction: ethers.utils.Deferrable<ethers.providers.TransactionRequest>
  ) {
    this.log(`SendUncheckedTransaction << "${JSON.stringify(transaction)}"`);
    await this.checkConnected();

    const txHash = await this.provider!.getSigner().sendUncheckedTransaction(
      transaction
    );
    this.log(`SendUncheckedTransaction >> "${txHash ?? 'undefined'}"`);

    return txHash;
  }

  async getChainId(): Promise<number> {
    await this.checkConnected();
    return this.provider!.getNetwork().then((network) => network.chainId);
  }

  async switchChain(chainId: number): Promise<void> {
    await this.checkConnected();
    // TODO: Check if the provider already know the chainId, if not use the wallet_addEthereumChain method to add it
    // https://docs.metamask.io/guide/rpc-api.html#wallet-addethereumchain
    await this.provider!.send('wallet_switchEthereumChain', [
      { chainId: `0x${chainId}` },
    ]);
  }

  async disconnect(): Promise<void> {
    await this.wagmiClient.connector?.disconnect();
    this.wagmiClient.clearState();
    this.wagmiClient.destroy();

    await this.ethereumClient.disconnect();

    this.web3Modal.closeModal();

    // Clear the cache
    Web3ModalV2Connector.clearCache(this.wagmiPrefixKey);

    console.info('Web3ModalV2Connector cache cleared');

    this.isConnected = false;
    this.provider = undefined;
    this.log('Disconnected');
  }

  /**
   * Remove the current Web3Modal UI from the DOM
   */
  protected removeWeb3ModalUI(
    querySelector: string = this.getRelativeQuerySelector()
  ) {
    const w3mModal = document.querySelector(querySelector);
    if (w3mModal) {
      w3mModal.remove();
    } else {
      console.warn(
        `Could not found 'w3m-modal' element with uuid ${this.uuid}`
      );
    }

    const w3mStyles = document.querySelector('#w3m-styles');
    if (w3mStyles) {
      w3mStyles?.remove();
    }
  }

  /**
   * Extract the Web3Provider from the client data
   * @param data Wagmi client data
   * @returns Web3Provider
   */
  private getWeb3Provider(data: any): ethers.providers.Web3Provider {
    let web3Provider =
      data?.provider as unknown as ethers.providers.Web3Provider;
    if (!(web3Provider instanceof ethers.providers.Web3Provider)) {
      web3Provider = new ethers.providers.Web3Provider(data?.provider as any);
    }
    return web3Provider;
  }

  /**
   * Create an interval to alter the w3m-qrcode uri and logoSrc attributes
   * @param universalLink Universal link to append before the wc uri (e.g. https://arian.ee/wc?uri=)
   * @param updateQrF Function to update the qr code
   * @param logoSrc Logo to set
   * @param logoSrc_dark Optional dark logo to set (default: fallback to logoSrc)
   * @param autoClear If true, the interval will be cleared when the uri attribute is set
   * @param ms Optional interval time in ms (default: 200)
   * @returns Interval id to be cleared if needed
   */
  private attachQRInjector(
    universalLink: string,
    updateQrFn: UpdateQrFunction,
    logoSrc?: string,
    logoSrc_dark = logoSrc,
    autoClear = false,
    ms = 200
  ) {
    this.log('QRInjector attached');
    const injectorInt = setInterval(() => {
      const allW3mQrCode = querySelectorAllDeep(
        this.getRelativeQuerySelector('w3m-qrcode')
      );
      const w3mQrCode = allW3mQrCode.pop();
      if (w3mQrCode) {
        updateQrFn(
          w3mQrCode,
          universalLink,
          injectorInt,
          logoSrc,
          logoSrc_dark,
          autoClear
        );
      }
    }, ms);
    return injectorInt;
  }

  private updateQR: UpdateQrFunction = (
    w3mQrCode: HTMLElement,
    universalLink: string,
    injectorInt: NodeJS.Timer,
    logoSrc: string | undefined,
    _logoSrc_dark = logoSrc,
    autoClear = false
  ) => {
    let wcUri: string | null = null;
    // Set the uri attribute
    const w3mQrCodeUri = w3mQrCode.getAttribute('uri');
    if (w3mQrCodeUri?.startsWith('wc:')) {
      wcUri = w3mQrCodeUri;
    }
    const targetUri = `${universalLink}${wcUri}`;
    const isW3mQrCodeUriSet = w3mQrCodeUri == targetUri;
    if (!isW3mQrCodeUriSet && wcUri != null) {
      w3mQrCode.setAttribute('uri', targetUri);
    }
    // Set the logoSrc attribute
    const w3mQrCodeLogoSrc = w3mQrCode.getAttribute('logoSrc');
    const isW3mQrCodeLogoSrcSet = !logoSrc || w3mQrCodeLogoSrc == logoSrc;
    if (!isW3mQrCodeLogoSrcSet) {
      w3mQrCode.setAttribute('logoSrc', logoSrc);
    }
    if (isW3mQrCodeUriSet && isW3mQrCodeLogoSrcSet && autoClear) {
      clearInterval(injectorInt);
      this.log('QRInjector detached');
    }
  };

  private userChoice: Web3ModalV2DesktopBehaviorMode | null = null;
  private updateQRChoice: UpdateQrFunction = (
    w3mQrCode: HTMLElement,
    universalLink: string,
    injectorInt: NodeJS.Timer,
    logoSrc?: string,
    logoSrc_dark?: string,
    autoClear = false
  ) => {
    if (w3mQrCode.style.display == 'none') return;
    if (this.userChoice !== null) {
      switch (this.userChoice) {
        case Web3ModalV2DesktopBehaviorMode.ArianeeOnly:
          this.updateQR(
            w3mQrCode,
            universalLink,
            injectorInt,
            logoSrc,
            logoSrc_dark,
            autoClear
          );
          return;
        case Web3ModalV2DesktopBehaviorMode.WalletConnectOnly:
          return;
      }
    }

    w3mQrCode.style.display = 'none';
    const qrContainer = querySelectorDeep(
      this.getRelativeQuerySelector('.w3m-qr-container')
    );
    if (qrContainer) {
      const arnContent = document.createElement('div');
      arnContent.className = 'arn-wc-content';
      arnContent.innerHTML = `<style>${
        Web3ModalV2Connector.qrChoiceStyle
      }</style>${this.qrChoiceHtml(
        this.desktopBehaviorOpts.primaryText,
        this.desktopBehaviorOpts.secondaryText,
        logoSrc,
        logoSrc_dark
      )}`;
      qrContainer.append(arnContent);
      const primaryButton = querySelectorDeep(
        this.getRelativeQuerySelector('.arn-wc-button.arn-wc-primary')
      );
      if (primaryButton) {
        primaryButton.onclick = () => {
          this.userChoice = Web3ModalV2DesktopBehaviorMode.ArianeeOnly;
          arnContent.remove();
          w3mQrCode.style.display = '';
          this.updateQR(
            w3mQrCode,
            universalLink,
            injectorInt,
            logoSrc,
            logoSrc_dark,
            autoClear
          );
        };
      }
      const secondaryButton = querySelectorDeep(
        this.getRelativeQuerySelector('.arn-wc-button.arn-wc-secondary')
      );
      if (secondaryButton) {
        secondaryButton.onclick = () => {
          this.userChoice = Web3ModalV2DesktopBehaviorMode.WalletConnectOnly;
          arnContent.remove();
          w3mQrCode.style.display = '';
        };
      }
    }
    if (autoClear) {
      clearInterval(injectorInt);
      this.log('QRInjector detached');
    }
  };

  private getRelativeQuerySelector(query?: string) {
    return `w3m-modal[arn-uuid="${this.uuid}"]${query ? ` ${query}` : ''}`;
  }

  /**
   * Create an interval to remove the w3m-modal-footer element
   * @param ms Optional interval time in ms (default: 200)
   * @returns Interval id to be cleared if needed
   */
  private attachDesktopWalletInjector(ms = 200) {
    this.log('DesktopWalletInjector attached');

    const injectorInt = setInterval(() => {
      const w3mFooter = querySelectorDeep(
        this.getRelativeQuerySelector(
          'w3m-desktop-wallet-selection > w3m-modal-footer'
        )
      );
      if (w3mFooter) {
        w3mFooter.remove();
        //clearInterval(injectorInt);
        //this.log('DesktopWalletInjector detached');
      }
    }, ms);

    return injectorInt;
  }

  /**
   * Create an interval to alter the w3m-modal-toast open attribute
   * @param ms Optional interval time in ms (default: 100)
   * @returns Interval id to be cleared if needed
   */
  private attachModalInjector(ms = 100) {
    this.log('ModalInjector attached');

    const injectorInt = setInterval(() => {
      const w3mToast = querySelectorDeep(
        this.getRelativeQuerySelector('w3m-modal-toast')
      );
      if (w3mToast) {
        const errorToast = w3mToast.shadowRoot?.querySelector('div.w3m-error');
        if (errorToast) {
          const errorToastMsg = errorToast.textContent;
          if (errorToastMsg?.includes('preloading')) {
            errorToast.remove();
            clearInterval(injectorInt);
            this.log('ModalInjector detached');
          }
        }
      }
    }, ms);

    return injectorInt;
  }

  /**
   * Create an interval to remove the WALLETCONNECT_DEEPLINK_CHOICE_KEY from localStorage
   * @param ms Optional interval time in ms (default: 100)
   * @returns Interval id to be cleared if needed
   */
  /*
  private attachLocalStorageInjector(ms = 100) {
    this.log('LocalStorageInjector attached');

    const injectorInt = setInterval(() => {
      if (
        localStorage.getItem(
          Web3ModalV2Connector.WALLETCONNECT_DEEPLINK_CHOICE_KEY
        ) !== null
      ) {
        localStorage.removeItem(
          Web3ModalV2Connector.WALLETCONNECT_DEEPLINK_CHOICE_KEY
        );
        this.log('Removed WALLETCONNECT_DEEPLINK_CHOICE_KEY from localStorage');
      }
    }, ms);

    return injectorInt;
  }
  */

  /**
   * Override the window.open function to intercept all universal links opening and force them to be opened in a new window
   * After some time, the window is closed if still open (to prevent user misundertanding)
   */
  /*
  private overrideWindowOpen() {
    const windowAsAny = window as any;
    // Preventing the function to be overridden multiple times
    if (!windowAsAny.ARN_OpenWindow) {
      windowAsAny.ARN_OpenWindow = window.open;
    }
    const openWindow = windowAsAny.ARN_OpenWindow;

    window.open = (url?: string | URL, target?: string, features?: string) => {
      const urlAsString = url?.toString();
      if (
        urlAsString &&
        urlAsString != '' &&
        customMobileWallets().some((wallet) =>
          urlAsString.startsWith(wallet.links.universal)
        )
      ) {
        const windowUuid = uuidv4();
        this.log(`Opening '${urlAsString}' in a new window (${windowUuid})`);
        const universalLinkWindow = openWindow(url, '_blank') as Window;

        // Improving user experience by showing a message in the new window
        let targetWindowIsBlank = false;
        setTimeout(() => {
          try {
            universalLinkWindow?.document.write(
              HTML_TEMPLATE_UNIVERSAL_LINK_WINDOW
            );
            // If we are able to write in the window, it means that the new window is blank (indicating that a native app link has been handled by the OS)
            targetWindowIsBlank = true;
          } catch {
            // NOP
          }

          // Close the window after few seconds if still open
          setTimeout(
            () => {
              this.log(`Closing window if still open (${windowUuid})`);
              universalLinkWindow?.close();
            },
            targetWindowIsBlank ? 3000 : 12000
          );
        }, 1000);

        return universalLinkWindow;
      } else {
        return openWindow(url, target, features);
      }
    };
  }
  */

  static clearCache(wagmiPrefixKey: string) {
    // Remove all wagmiPrefixKey.* keys from localStorage
    // This is also removing wagmiPrefixKey.walletconnect (${wagmiPrefixKey}.${WebModalV2Connector.WALLETCONNECT_KEY})
    for (const key in localStorage) {
      if (key.startsWith(`${wagmiPrefixKey}.`)) {
        localStorage.removeItem(key);
      }
    }

    localStorage.removeItem(Web3ModalV2Connector.WALLETCONNECT_KEY);
    localStorage.removeItem(
      Web3ModalV2Connector.WALLETCONNECT_DEEPLINK_CHOICE_KEY
    );
    localStorage.removeItem(Web3ModalV2Connector.W3M_RECENT_WALLET);
  }

  private static readonly qrChoiceStyle = `
  .arn-wc-button {
    width: 100%;
    appearance: none;
    background-color: inherit;
    border-radius: 0.5em;
    border: 1.5px solid var(--w3m-color-fg-3);
    cursor: pointer;
    display: block;
  }
  .arn-wc-button:hover {
    background-color: var(--w3m-color-bg-2);
  }
  .arn-wc-primary {
    margin-bottom: 0.5em;
    font-weight: 600;
    padding: 2em;
  }
  .arn-wc-secondary {
    margin-top: 0.5em;
    padding: 1em;
  }
  .arn-wc-text {
    color: var(--w3m-color-fg-1);
    display: block;
    margin-top: 0.5em;
  }
  .arn-wc-content {
    --contentMargin: 0;
    --contentMaxWidth: 100%;
    --imageMaxWidth: 120px;
    width: 100%;
    padding: var(--contentMargin);
    background-color: var(--w3m-color-bg-1);
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    align-content: space-around;
    justify-content: space-evenly;
  }
  .arn-wc-content {
    max-width: var(--contentMaxWidth);
  }
  .arn-wc-image {
    max-width: var(--imageMaxWidth);
    display: flex;
    justify-content: space-evenly;
    align-items: center;
    border-radius: 0.5em;
    margin: auto;
  }
  .arn-wc-image img,
  .arn-wc-image picture {
    border-radius: 0.5em;
    width: 100%;
  }
  .arn-wc-secondary .arn-wc-image {
    width: 60px;
    padding: 8px;
    height: 60px;
    background-color: rgb(88,132,233);
  }
  `;

  private readonly qrChoiceHtml = (
    primaryText?: string,
    secondaryText?: string,
    primaryLogoUrl?: string,
    primaryLogoUrl_dark?: string
  ) => {
    const primaryTextElement = primaryText
      ? `<span class="arn-wc-text">${primaryText}</span>`
      : '';
    const secondaryTextElement = secondaryText
      ? `<span class="arn-wc-text">${secondaryText}</span>`
      : '';
    return `<button class="arn-wc-button arn-wc-primary">
    <div class="arn-wc-image">
      <picture>
        <source srcset="${primaryLogoUrl_dark}" media="(prefers-color-scheme:dark)">
        <img src="${primaryLogoUrl}" alt="${primaryText}">
      </picture>
    </div>
    ${primaryTextElement}
  </button>
  <button class="arn-wc-button arn-wc-secondary">
    <div class="arn-wc-image">
      <img src="https://storage.googleapis.com/arn3-static-resources/yslbeauty/walletconnect/wallet%20connect.svg" alt="${secondaryText}"/>
    </div>
    ${secondaryTextElement}
  </button>`;
  };
}
